import axios from 'axios';
import { config } from '../config/environment';

const ACCESS_KEY = 'fleet-access-token';
const REFRESH_KEY = 'fleet-refresh-token';

/* Yenileme isteği global TIMEOUT'a (30 sn) bırakılmaz: asılı kalan tek bir yenileme
   tüm panelin isteklerini kilitler. Partner web ile aynı eşik. */
const REFRESH_TIMEOUT = 12000;

/* Sunucu erişim jetonunu kısa ömürlü üretiyor. İstek gönderilmeden ÖNCE bu pencere
   içinde kalan jeton yenilenir; aksi hâlde istek yolda ölür ve 401 turu başlar. */
const PROACTIVE_SKEW_SECONDS = 60;

export const getAuthToken = () => localStorage.getItem(ACCESS_KEY) || '';

export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY) || '';

export const setTokens = (accessToken, refreshToken) => {
  if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
};

const clearCookies = () => {
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim();
    if (!name) return;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
  });
};

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  clearCookies();
};

const decodeJwtPayload = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const getTokenPayload = (token) => decodeJwtPayload(token || getAuthToken());

export const isTokenExpired = (token, skewSeconds = PROACTIVE_SKEW_SECONDS) => {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (!exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return exp <= now + skewSeconds;
};

/* ------------------------------------------------------------------------------
   TEK-UÇUŞ (single-flight) YENİLEME

   Sunucudaki refresh jetonu TEK KULLANIMLIK: AuthService.RefreshTokenAsync gelen
   jetonu revoke edip yenisini üretiyor. Aynı jetonla ikinci bir istek gitmesi
   "geçersiz jeton" cevabı doğurur ve oturumu düşürür.

   Filo panelinde yenilemeyi tetikleyen ÜÇ ayrı yol var (axios 401 interceptor'ı,
   SignalR handleAuthError, SignalR accessTokenFactory) — üçü de burada birleşir.
   Ayrıca birden fazla sekme aynı localStorage'ı paylaştığı için sekmeler arası
   Web Locks kilidi kullanılır: aynı anda yalnız bir sekme jetonu yakar.
------------------------------------------------------------------------------- */

const REFRESH_LOCK_NAME = 'fleet-token-refresh';

let inflight = null;

const withCrossTabLock = (fn) => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, fn);
  }
  return fn();
};

/* Oturum gerçekten bitti: jetonlar silinir, çıkış yayılır. TEK yer burasıdır —
   çağıranlar ayrıca clearTokens() çağırmaz, yoksa yeni yazılmış geçerli jeton
   silinebilir. */
const failFatal = (reason, extra = {}) => {
  clearTokens();
  window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason, ...extra } }));
  return { success: false, fatal: true, reason, ...extra };
};

const requestNewTokens = async (refreshToken) => {
  let response;

  try {
    response = await axios.post(
      `${config.API_BASE_URL}/api/Auth/refresh-token?refreshToken=${encodeURIComponent(refreshToken)}`,
      {},
      {
        headers: { 'Content-Type': 'application/json', Accept: '*/*', 'X-Client-Channel': 'web' },
        timeout: REFRESH_TIMEOUT,
      }
    );
  } catch (error) {
    const status = error?.response?.status;

    /* Ağ hatası / zaman aşımı / 5xx / Cloudflare kesintisi oturumun bittiği anlamına
       GELMEZ. Jetonlara dokunulmaz; çağıran ister tekrar dener, ister isteği düşürür.
       Eski davranış burada clearTokens() çağırıyordu — "token devamlı düşüyor"un
       ikinci kaynağı buydu. */
    if (status !== 401 && status !== 403) {
      return { success: false, fatal: false, reason: 'refresh_unavailable', status, error };
    }

    return failFatal('refresh_rejected', { status });
  }

  /* Geçersiz/süresi dolmuş refresh jetonu gövdede gelir: ApiControllerBase başarısız
     ServiceResult'ı HTTP 200 + { success: false } olarak döndürüyor. */
  const body = response?.data;
  const data = body?.data || {};
  const accessToken = data.accessToken || data.access_token || '';

  if (body?.success === false || !accessToken) {
    return failFatal('refresh_rejected', { message: body?.message });
  }

  const newRefreshToken = data.refreshToken || data.refresh_token || '';
  const effectiveRefresh = newRefreshToken || refreshToken;

  setTokens(accessToken, effectiveRefresh);

  window.dispatchEvent(
    new CustomEvent('auth:token_refreshed', {
      detail: { accessToken, refreshToken: effectiveRefresh },
    })
  );

  return { success: true, accessToken, refreshToken: effectiveRefresh };
};

export const refreshAccessToken = async () => {
  if (inflight) return inflight;

  /* Kilit sırası bize gelene kadar BAŞKA bir sekme yenilemiş olabilir. Ölçüt jetonun
     ömrü değil DEĞİŞMİŞ olması: elimizdekinden farklı bir jeton yazılmışsa yenileme
     çoktan yapılmıştır ve tek kullanımlık refresh jetonunu yakmaya gerek yoktur. */
  const tokenBefore = getAuthToken();

  inflight = (async () => {
    try {
      return await withCrossTabLock(async () => {
        const current = getAuthToken();
        if (current && current !== tokenBefore) {
          return { success: true, accessToken: current, refreshToken: getRefreshToken(), reused: true };
        }

        const refreshToken = getRefreshToken();
        if (!refreshToken) return failFatal('no_refresh');

        return requestNewTokens(refreshToken);
      });
    } finally {
      inflight = null;
    }
  })();

  return inflight;
};

export const ensureValidAccessToken = async ({ forceRefresh = false } = {}) => {
  const accessToken = getAuthToken();
  const refreshToken = getRefreshToken();

  if (!forceRefresh && accessToken && !isTokenExpired(accessToken, PROACTIVE_SKEW_SECONDS)) {
    return { success: true, accessToken, refreshToken };
  }

  if (!refreshToken) {
    return accessToken ? { success: true, accessToken, refreshToken } : { success: false, fatal: true, reason: 'no_access' };
  }

  return refreshAccessToken();
};

export const isAuthenticated = () => !!getAuthToken();

export const logout = () => {
  clearTokens();
  window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'manual' } }));
};
